import React from 'react';
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowLeft, Calendar, MoreVertical, UserCheck, ShieldAlert, ShieldCheck, Shield } from 'lucide-react';
import { useLanguage } from '@/context/language-context';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { blockUser, unblockUser } from '@/services/blockService';
import { useToast } from '@/hooks/use-toast';

interface ChatHeaderProps {
  name: string;
  avatar?: string | null;
  type?: 'escort' | 'client';
  isVerified?: boolean;
  isPartnerOnline?: boolean;
  onBack?: () => void;
  onBookAppointment?: () => void;
  onViewProfile?: () => void;
  isMobile?: boolean;
  blockStatus: 'blocked_by_partner' | 'blocked_by_user' | 'none' | 'checking';
  partnerId: string;
  refreshBlockStatus: () => void;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({
  name,
  avatar,
  type,
  isVerified,
  isPartnerOnline,
  onBack,
  onBookAppointment,
  onViewProfile,
  isMobile = false,
  blockStatus,
  partnerId,
  refreshBlockStatus
}) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  
  const handleBlockUser = async () => {
    try {
      const result = await blockUser(partnerId);
      if (result.success) {
        toast({
          title: t('User blocked'),
          description: t('You will no longer receive messages from this user'),
          variant: 'default',
        });
        refreshBlockStatus();
      } else {
        throw result.error;
      }
    } catch (error) {
      console.error('Failed to block user:', error);
      toast({
        title: t('Error'),
        description: t('Failed to block user'),
        variant: 'destructive',
      });
    }
  };
  
  const handleUnblockUser = async () => {
    try {
      const result = await unblockUser(partnerId);
      if (result.success) {
        toast({
          title: t('User unblocked'),
          description: t('You can now exchange messages with this user'),
          variant: 'default',
        });
        refreshBlockStatus();
      } else {
        throw result.error;
      }
    } catch (error) {
      console.error('Failed to unblock user:', error);
      toast({
        title: t('Error'),
        description: t('Failed to unblock user'),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="border-b p-3 flex items-center justify-between">
      <div className="flex items-center gap-3">
        {isMobile && onBack && (
          <Button variant="ghost" size="icon" onClick={onBack} className="mr-1">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        <Avatar>
          <AvatarImage src={avatar || "/placeholder-user.png"} alt={name} />
          <AvatarFallback>{name[0]}</AvatarFallback>
        </Avatar>
        <div>
          <div className="flex items-center gap-1">
            <h2 className="text-base font-semibold">{name}</h2>
            {isVerified && (
              <UserCheck size={16} className="text-blue-500" />
            )}
            {blockStatus === 'blocked_by_user' && (
              <ShieldAlert size={16} className="text-red-500" />
            )}
            {blockStatus === 'blocked_by_partner' && (
              <Shield size={16} className="text-red-500" />
            )}
          </div>
          <div className="flex items-center text-xs text-muted-foreground">
            {isPartnerOnline ? (
              <>
                <span className="w-2 h-2 rounded-full bg-green-500 mr-1.5"></span>
                {t('Online')}
              </>
            ) : (
              t('Offline')
            )}
            {type && <span className="ml-2">• {type === 'escort' ? t('Escort') : t('Client')}</span>}
          </div>
        </div>
      </div>
      
      <div className="flex items-center">
        {type === 'escort' && onBookAppointment && (
          <Button variant="outline" size="sm" onClick={onBookAppointment} className="mr-2">
            <Calendar className="h-4 w-4 mr-1" />
            {t('Book')}
          </Button>
        )}
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{t('Actions')}</DropdownMenuLabel>
            
            {onViewProfile && (
              <DropdownMenuItem onClick={onViewProfile}>
                {t('View profile')}
              </DropdownMenuItem>
            )}
            
            <DropdownMenuSeparator />
            
            {blockStatus === 'blocked_by_user' ? (
              <DropdownMenuItem onClick={handleUnblockUser} className="text-green-600">
                <ShieldCheck className="h-4 w-4 mr-2" />
                {t('Unblock user')}
              </DropdownMenuItem>
            ) : blockStatus === 'blocked_by_partner' ? (
              <DropdownMenuItem disabled className="text-muted-foreground">
                <Shield className="h-4 w-4 mr-2" />
                {t('Blocked by user')}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={handleBlockUser} className="text-red-600">
                <ShieldAlert className="h-4 w-4 mr-2" />
                {t('Block user')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default ChatHeader;
